/**
 * Email Tab Component
 *
 * Displays current email (read-only) and allows the user to change it
 * via the EmailVerifyDialog OTP flow.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Mail, CheckCircle2, XCircle } from "lucide-react";
import type { User } from "@/services/authService";
import { EmailVerifyDialog } from "./EmailVerifyDialog";
import { useAuth } from "@/contexts/AuthContext";

interface EmailTabProps {
  user: User;
}

export function EmailTab({ user }: EmailTabProps) {
  const { refreshUser } = useAuth();
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleVerified = async () => {
    await refreshUser();
  };

  return (
    <div className="space-y-4">
      {/* Important Email Warning */}
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm">
        <p className="font-semibold text-amber-900 mb-2">⚠️ Peringatan Penting Email</p>
        <p className="text-amber-800 mb-2">
          Email ini untuk verifikasi akun ORARI, komunikasi resmi, dan notifikasi pembayaran iuran/ SPP.
        </p>
        <p className="text-amber-800 text-xs italic">
          Pastikan email valid & aktif. ORARI tidak bertanggung jawab jika email salah / tidak bisa diakses.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Email</CardTitle>
          <CardDescription>
            Email digunakan untuk verifikasi akun dan komunikasi resmi ORARI.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status badge */}
          <div className="flex items-center gap-2">
            {user.isUpdatedEmail ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700 dark:text-green-400">Email terverifikasi</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-red-500" />
                <span className="text-sm text-red-600 dark:text-red-400">Email belum terverifikasi</span>
              </>
            )}
          </div>

          {/* Read-only email field */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-muted-foreground">Alamat Email</label>
            <div className="flex items-center gap-2 rounded-md border border-input bg-muted/40 px-3 py-2">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm">{user.email || "—"}</span>
            </div>
          </div>

          {/* Change button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Ubah Email
            </button>
          </div>
        </CardContent>
      </Card>

      <EmailVerifyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={user}
        onVerified={handleVerified}
        dismissible
      />
    </div>
  );
}
